/**
 * frontend/src/pages/CorpusStatsPage.js
 *
 * Public-facing Corpus Statistics page.
 * Shows live verse counts per tradition, links to browse pages.
 * Dynamic — updates as corpus grows via ingestion.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../services/ApiService';
import styles from './CorpusStatsPage.module.css';

// Map corpus group id → browse route (catId/subId)
const BROWSE_ROUTES = {
  veda:        '/browse/veda',
  upanishad:   '/browse/upanishad',
  gita:        '/browse/gita/gita_text',
  brahmasutra: '/browse/vedanta/brahmasutra',
  shankara:    '/browse/shankara',
  ramayana:    '/browse/itihasa',
  mahabharata: '/browse/itihasa',
  bhagavata:   '/browse/purana/srimad_bhagavata',
  purana:      '/browse/purana',
  darshana:    '/browse/vedanta',
  stotra:      '/browse/kavya/narayaneeyam',
  kavya:       '/browse/kavya',
  telugu:      '/browse/telugu',
  tantra:      '/browse/shankara/stotras',
};

function formatCount(n) {
  if (n >= 100000) return (n / 1000).toFixed(0) + 'k';
  if (n >= 10000)  return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}

function CorpusCard({ group, index }) {
  const route = BROWSE_ROUTES[group.id] || '/browse';
  return (
    <Link
      to={route}
      className={styles.card}
      style={{ '--card-color': group.color, animationDelay: `${index * 60}ms` }}
    >
      <div className={styles.cardGlow} />
      <div className={styles.cardTop}>
        <span className={styles.cardDeva}>{group.label}</span>
        <span className={styles.cardTradition}>{group.tradition}</span>
      </div>
      <div className={styles.cardIast}>{group.labelIast}</div>
      <div className={styles.cardDesc}>{group.description}</div>
      <div className={styles.cardFooter}>
        <div className={styles.cardStat}>
          <span className={styles.statNumber}>{formatCount(group.verse_count)}</span>
          <span className={styles.statLabel}>verses</span>
        </div>
        <div className={styles.cardStat}>
          <span className={styles.statNumber}>{group.text_count}</span>
          <span className={styles.statLabel}>texts</span>
        </div>
        <div className={styles.cardArrow}>→</div>
      </div>
      <div className={styles.cardBar}>
        <div className={styles.cardBarFill} />
      </div>
    </Link>
  );
}

function StatPill({ value, label }) {
  return (
    <div className={styles.pill}>
      <span className={styles.pillValue}>{value}</span>
      <span className={styles.pillLabel}>{label}</span>
    </div>
  );
}

export default function CorpusStatsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    ApiService.getCorpusStats()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerEyebrow}>वाक्य · Vakya</div>
          <h1 className={styles.headerTitle}>
            <span className={styles.titleDeva}>महावाक्य</span>
            <span className={styles.titleSub}>Classical Verse Corpus</span>
          </h1>
          <p className={styles.headerDesc}>
            A living archive of Sanskrit and Telugu classical literature —
            Upaniṣads, Purāṇas, Itihāsa, Kāvya, and the complete works of Ādi Śaṅkara.
            Growing continuously.
          </p>

          {/* Live stats pills */}
          {data && (
            <div className={styles.pills}>
              <StatPill value={data.grand_total.toLocaleString()} label="total verses" />
              <StatPill value={data.group_count} label="traditions" />
              <StatPill value={data.groups.reduce((s, g) => s + g.text_count, 0)} label="texts" />
            </div>
          )}
          {loading && <div className={styles.pillsLoading}>Loading corpus data…</div>}
        </div>
        <div className={styles.headerOrnament}>॥</div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {error && (
          <div className={styles.error}>
            Could not load corpus stats. Please try again.
          </div>
        )}

        {loading && (
          <div className={styles.loadingGrid}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.cardSkeleton} />
            ))}
          </div>
        )}

        {data && (
          <>
            <div className={styles.grid}>
              {data.groups.map((group, i) => (
                <CorpusCard key={group.id} group={group} index={i} />
              ))}
            </div>

            <div className={styles.footer}>
              <p className={styles.footerNote}>
                ॥ Corpus updated continuously · {new Date(data.generated_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' })} ॥
              </p>
              <p className={styles.footerSub}>
                Sources: GRETIL · Vālmīki Critical Edition · BORI Mahābhārata · Ananda Ashrama · Sanskrit Documents
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
